import { useCallback, useEffect, useRef } from "react";

interface UseWatchdogOptions {
	/** How long (ms) to wait without a `pet()` before `onTimeout` fires. */
	timeoutMs: number;
	/** Invoked once when the watchdog expires (no `pet()` within `timeoutMs`). */
	onTimeout: () => void;
}

export interface WatchdogControls {
	/** Arm (or re-arm) the countdown. Call when the watched activity begins. */
	start: () => void;
	/** Reset the countdown. Call on every progress signal to keep it alive. */
	pet: () => void;
	/** Disarm the watchdog so no timeout fires until `start()`/`pet()` again. */
	stop: () => void;
}

/**
 * Generic watchdog timer. Fires `onTimeout` if `pet()` is not called within
 * `timeoutMs` of the last `start()`/`pet()`.
 *
 * Use it to detect a stalled pipeline — e.g. video frames streaming out to a
 * server but no responses coming back — and recover gracefully (show a retry,
 * disconnect, log) instead of leaving the UI frozen forever.
 *
 * The pending timer is cleared automatically on unmount. `onTimeout` is read
 * through a ref, so passing a fresh inline callback each render never restarts
 * an already-armed timer.
 */
function useWatchdog({ timeoutMs, onTimeout }: UseWatchdogOptions): WatchdogControls {
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const onTimeoutRef = useRef(onTimeout);

	useEffect(() => {
		onTimeoutRef.current = onTimeout;
	}, [onTimeout]);

	const stop = useCallback(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
	}, []);

	const arm = useCallback(() => {
		if (timerRef.current) clearTimeout(timerRef.current);
		timerRef.current = setTimeout(() => {
			timerRef.current = null;
			onTimeoutRef.current();
		}, timeoutMs);
	}, [timeoutMs]);

	// Clear any pending timer when the consuming component unmounts.
	useEffect(() => stop, [stop]);

	return { start: arm, pet: arm, stop };
}

export default useWatchdog;
